import house from "../assets/images/shutterstock_2493365641-min.jpg";
import constants from "../constants/constants";
import { useState } from "react";
import { pageSEO, SEO } from "../components/seo.component";

export default function Evaluation() {
  const [popup, setPopup] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    propertyAddress: "",
    city: "",
    provinceState: "",
    propertySize: "",
    lotSize: "",
    propertyType: "",
    buildingType: "",
    notes: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Form validation function
  const validateForm = () => {
    const errors = {};

    // Check required fields
    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    if (!formData.phone.trim()) {
      errors.phone = "Phone is required";
    }

    if (!formData.propertyAddress.trim()) {
      errors.propertyAddress = "Property address is required";
    }

    if (!formData.city.trim()) {
      errors.city = "City is required";
    }

    if (!formData.provinceState.trim()) {
      errors.provinceState = "Province/State is required";
    }

    if (!formData.propertySize.trim()) {
      errors.propertySize = "Property size is required";
    }

    if (!formData.lotSize.trim()) {
      errors.lotSize = "Lot size is required";
    }

    if (!formData.propertyType.trim()) {
      errors.propertyType = "Property type is required";
    }

    if (!formData.buildingType.trim()) {
      errors.buildingType = "Building type is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Create HTML message body with all form data
    const htmlMessage = `
      <h2 style="color: #2563eb; margin-bottom: 20px;">New Home Evaluation Request</h2>
      
      <div style="background-color: #f8fafc; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
        <h3 style="color: #1e293b; margin-bottom: 15px;">Contact Information</h3>
        <p><strong>Name:</strong> ${formData.name}</p>
        <p><strong>Email:</strong> ${formData.email}</p>
        <p><strong>Phone:</strong> ${formData.phone}</p>
      </div>

      <div style="background-color: #f8fafc; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
        <h3 style="color: #1e293b; margin-bottom: 15px;">Property Details</h3>
        <p><strong>Address:</strong> ${formData.propertyAddress}</p>
        <p><strong>City:</strong> ${formData.city}</p>
        <p><strong>Province/State:</strong> ${formData.provinceState}</p>
        <p><strong>Property Size:</strong> ${formData.propertySize}</p>
        <p><strong>Lot Size:</strong> ${formData.lotSize}</p>
        <p><strong>Property Type:</strong> ${formData.propertyType}</p>
        <p><strong>Building Type:</strong> ${formData.buildingType}</p>
      </div>

      ${formData.notes ? `
      <div style="background-color: #f8fafc; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
        <h3 style="color: #1e293b; margin-bottom: 15px;">Additional Notes</h3>
        <p style="white-space: pre-wrap;">${formData.notes}</p>
      </div>
      ` : ''}

      <div style="background-color: #dcfce7; padding: 15px; border-radius: 8px; border-left: 4px solid #16a34a;">
        <p style="margin: 0; color: #166534; font-weight: 600;">
          This evaluation request was submitted from the website contact form.
        </p>
      </div>
    `;

    const emailFormData = new FormData();
    emailFormData.append('data', JSON.stringify({
      recipientEmails: [constants.AGENT_EMAIL],
      senderEmail: process.env.REACT_APP_AGENT_EMAIL,
      subject: "New Home Evaluation Request",
      content: htmlMessage,
      senderName: formData.name,
      replyTo: formData.email,
    }));

    let EmailApiUrl = process.env.REACT_APP_NODE_ENV === "production" ? "https://3.138.143.209/global/send-email" : "http://3.138.143.209/global/send-email";

    try {
      const response = await fetch(EmailApiUrl, {
        method: "POST",
        body: emailFormData,
      });

      if (!response.ok) {
        throw new Error("Failed to send email");
      }

      const res = await response.json();
      console.log("res", res);

      if (res?.message === "success") {
        console.log("Success", res);
        setPopup({
          subject: "Thanks for your message!",
          message: "We will get back to you as soon as possible",
        });
        // Clear form after successful submission
        setFormData({
          name: "",
          email: "",
          phone: "",
          propertyAddress: "",
          city: "",
          provinceState: "",
          propertySize: "",
          lotSize: "",
          propertyType: "",
          buildingType: "",
          notes: "",
        });
        setFormErrors({});
      } else {
        setPopup({
          subject: "We encountered an error in sending this message",
          message: "Please try again later",
        });
      }
    } catch (err) {
      console.log(err);
      setPopup({
        subject: "We encountered an error in sending this message",
        message: "Please try again later",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const seoConfig = pageSEO.evaluation();

  return (
    <>
      <SEO {...seoConfig} />
      <div className="max-w-[1100px] pt-[150px] mx-auto grid md:grid-cols-2 px-6 gap-x-24 gap-y-10 min-h-[70vh] mb-12">
        <div>
          <h1 className="text-4xl text-black libre">Free Home Evaluation</h1>

          <p className="font-semibold text-med-grey my-5">
            Wondering how much your home would sell for in today's market?
            Please complete the form below and I would be happy to send you a
            Free Home Evaluation.
          </p>
          <form onSubmit={handleSubmit}>
            <div className="form-item">
              <label className="form-label">Name*</label>
              <input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`form-input ${formErrors.name ? 'border-red-500' : ''}`}
                placeholder="Full Name"
              />
              {formErrors.name && (
                <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>
              )}
            </div>
            <div className="form-item">
              <label className="form-label">Email*</label>
              <input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`form-input ${formErrors.email ? 'border-red-500' : ''}`}
                placeholder="Email Address"
              />
              {formErrors.email && (
                <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
              )}
            </div>
            <div className="form-item">
              <label className="form-label">Phone*</label>
              <input
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className={`form-input ${formErrors.phone ? 'border-red-500' : ''}`}
                placeholder="Phone Number"
              />
              {formErrors.phone && (
                <p className="text-red-500 text-sm mt-1">{formErrors.phone}</p>
              )}
            </div>
            <div className="form-item">
              <label className="form-label">Property Address*</label>
              <input
                name="propertyAddress"
                value={formData.propertyAddress}
                onChange={handleInputChange}
                className={`form-input ${formErrors.propertyAddress ? 'border-red-500' : ''}`}
                placeholder="Address of the Property"
              />
              {formErrors.propertyAddress && (
                <p className="text-red-500 text-sm mt-1">{formErrors.propertyAddress}</p>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-x-10">
              <div className="form-item">
                <label className="form-label">City*</label>
                <input
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className={`form-input ${formErrors.city ? 'border-red-500' : ''}`}
                  placeholder="City of the Property"
                />
                {formErrors.city && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.city}</p>
                )}
              </div>

              <div className="form-item">
                <label className="form-label">Province/State*</label>
                <input
                  name="provinceState"
                  value={formData.provinceState}
                  onChange={handleInputChange}
                  className={`form-input ${formErrors.provinceState ? 'border-red-500' : ''}`}
                  placeholder="Province/State of the Property"
                />
                {formErrors.provinceState && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.provinceState}</p>
                )}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-x-10">
              <div className="form-item">
                <label className="form-label">Property Size*</label>
                <input
                  name="propertySize"
                  value={formData.propertySize}
                  onChange={handleInputChange}
                  className={`form-input ${formErrors.propertySize ? 'border-red-500' : ''}`}
                  placeholder="Approx. Square Footage"
                />
                {formErrors.propertySize && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.propertySize}</p>
                )}
              </div>
              <div className="form-item">
                <label className="form-label">Lot Size*</label>
                <input
                  name="lotSize"
                  value={formData.lotSize}
                  onChange={handleInputChange}
                  className={`form-input ${formErrors.lotSize ? 'border-red-500' : ''}`}
                  placeholder="Approx. Square Footage/Acreage"
                />
                {formErrors.lotSize && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.lotSize}</p>
                )}
              </div>
            </div>
            <div className="form-item">
              <label className="form-label">Property Type*</label>
              <select
                name="propertyType"
                value={formData.propertyType}
                onChange={handleInputChange}
                className={`form-input ${formErrors.propertyType ? 'border-red-500' : ''}`}
              >
                <option value="">Please Select</option>
                <option value="Residential">Residential</option>
                <option value="Commercial">Commercial</option>
                <option value="Land">Land</option>
                <option value="Parking">Parking</option>
                <option value="Investment">Investment</option>
                <option value="Agricultural">Agricultural</option>
              </select>
              {formErrors.propertyType && (
                <p className="text-red-500 text-sm mt-1">{formErrors.propertyType}</p>
              )}
            </div>
            <div className="form-item">
              <label className="form-label">Building Type*</label>
              <select
                name="buildingType"
                value={formData.buildingType}
                onChange={handleInputChange}
                className={`form-input ${formErrors.buildingType ? 'border-red-500' : ''}`}
              >
                <option value="">Please Select</option>
                <option value="House">House</option>
                <option value="Townhouse">Townhouse</option>
                <option value="Condo">Condo</option>
                <option value="Duplex">Duplex</option>
                <option value="Triplex">Triplex</option>
                <option value="Office">Office</option>
                <option value="Mobile Home">Mobile Home</option>
                <option value="Retail">Retail</option>
              </select>
              {formErrors.buildingType && (
                <p className="text-red-500 text-sm mt-1">{formErrors.buildingType}</p>
              )}
            </div>
            <div className="form-item">
              <label className="form-label">Other Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                className="form-input resize-none h-32"
                placeholder="Please tell me any other information you feel I should know about your property"
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`btn-secondary self-start px-10 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </form>
        </div>
        <div className="overflow-hidden h-[600px] ">
          <img className="h-full w-full object-cover" src={house} alt="House" />
        </div>
      </div>
      {popup && (
        <>
          <div className="backdrop" />
          <div className="popup">
            <h5 className="font-bold text-black text-lg">{popup.subject}</h5>
            <p className="font-semibold text-med-grey tracking-wide mb-3">
              {popup.message}
            </p>
            <button onClick={() => setPopup(false)} className="btn-secondary">
              Close
            </button>
          </div>
        </>
      )}
    </>
  );
}
